import React, { useEffect, useRef, useCallback } from "react";
import embed from "vega-embed";

import ButtonSimple from "@/components/ButtonSimple";
import TooltipCustom from "@/components/TooltipCustom";
import { handleExport, lineBreakerForChartTitle } from "@/utils";
import { IconAiOutlineDownload } from "@/utils/icons";

const ChartDonut = (props: any) => {

  const { chartData }: any = props;

  const chartRef: React.RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  const donutData = chartData?.data;
  const legendTitle = chartData?.legend_title || '';
  const breaked_donut = chartData?.breaked_donut || false;
  const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const viewRef = useRef<any>(null);
  const customColorDomain = chartData?.custom_color_domain_key || "";

  const calculateOptimalRadius = () => {

    if (!chartRef.current) {
      return { innerRadius: 70, outerRadius: 120 };
    }

    const containerWidth = chartRef.current.offsetWidth;
    const containerHeight = chartRef.current.offsetHeight;

    const labelSpace = 150; // Space needed for labels on each side
    const availableWidth = containerWidth - (labelSpace * 2);
    const availableHeight = containerHeight - 40; // Some padding

    // Calculate maximum possible radius
    const maxRadius = Math.min(availableWidth, availableHeight) / 2;

    // Ensure minimum sizes
    const minOuterRadius = 80;
    const maxOuterRadius = Math.max(minOuterRadius, maxRadius - 70);

    // Calculate inner radius (maintain donut thickness)
    const donutThickness = 50;
    const innerRadius = Math.max(30, maxOuterRadius - donutThickness);

    return {
      innerRadius: Math.min(innerRadius, maxOuterRadius - 20),
      outerRadius: maxOuterRadius
    };
  };

  const renderChart = useCallback(() => {

    if (!chartRef.current || !donutData) {
      return;
    }

    const { innerRadius, outerRadius } = calculateOptimalRadius();
    const chartWidth = chartRef.current.offsetWidth > 400 ? chartRef.current.offsetWidth - 220 : 400;
    const chartHeight = chartRef.current.offsetWidth > 400 ? chartRef.current.offsetWidth - 220 : 400;


    const title = chartData?.title || "";
    const formattedTitle = lineBreakerForChartTitle(title, chartWidth+100);

    const spec: any = {
      "$schema": "https://vega.github.io/schema/vega/v6.json",
      "description": "Donut with non-overlapping labels.",
      "width": chartWidth,
      "height": chartHeight,
      "autosize": "pad",
      "title": {
        "text": formattedTitle,
        "fontSize": 12,
        "fontFamily": "Arial",
        "fontWeight": "bold",
        "color": "black",
        "anchor": "middle",
        "lineHeight": 14
      },
      "padding": { "top": 0, "bottom": 0, "left": 0, "right": 10 },

      "signals": [
        {
          "name": "startAngle",
          "value": 0
        },
        {
          "name": "sortField",
          "update": "''",
          "description": "Sort field if desired"
        },
        {
          "name": "sortOrder",
          "update": "''",
          "description": "Sort order if desired (ascending or descending)"
        },
        {
          "name": "padAngle", "value": 0.02,
        },
        {
          "name": "labelFontSize",
          "init": "10",
          "description": "The font size used for labels"
        },
        {
          "name": "labelHeight",
          "update": "labelFontSize",
          "description": "This is approximately the same as the label font size but can be adjusted if you want extra padding"
        },
        { "name": "innerRadius", "value": 0 },
        { "name": "outerRadius", "value": outerRadius },
        // { "name": "outerRadius", "value": 170 },
        {
          "name": "counter",
          "description": "Counter used to loop through an array",
          "value": 0,
          "on": [
            {
              "events": { "type": "timer", "throttle": 0 },
              "update": "counter<length(data('labelPositions'))?counter + 1:counter"
            },
            { "events": { "signal": "leftRightCount" }, "update": "0" }
          ]
        },
        {
          "name": "shiftArray",
          "description": "An array of shift positions",
          "update": "{right: leftRightCount.right!=0? pluck(data('labelPositions')[0]['shiftArray'],'shift'):[], left: leftRightCount.left!=0? pluck(data('labelPositions')[leftRightCount.right]['shiftArray'],'shift'):[]}"
        },
        {
          "name": "leftRightCountArray",
          "description": "Array of label sides",
          "update": "reverse(sort( pluck( data('labelPositions'), 'side')))"
        },
        {
          "name": "leftRightCount",
          "description": "Count of  label sides",
          "update": "{right:lastindexof(leftRightCountArray,'right')+1,left:length(leftRightCountArray)- (lastindexof(leftRightCountArray,'right')+1)}"
        },
        {
          "name": "p1",
          "description": "Used to loop through position array one element at a time with a running sum but reseting the counter to 0 if it ever goes negative",
          "value": { "right": 0, "left": 0 },
          "on": [
            {
              "events": { "signal": "leftRightCount" },
              "update": "{'right': 0, 'left': 0}"
            },
            {
              "events": { "signal": "counter" },
              "update": "{right:shiftArray.right[counter-1]+p1.right<0?0:shiftArray.right[counter-1]+p1.right, left:shiftArray.left[counter-1]+p1.left<0?0:shiftArray.left[counter-1]+p1.left}",
              "force": true
            }
          ]
        },
        {
          "name": "p2",
          "description": "Used to reassemble the position array as a string with final shift positions",
          "value": { "right": [], "left": [] },
          "on": [
            {
              "events": { "signal": "leftRightCount" },
              "update": "{'right': [], 'left': []}"
            },
            {
              "events": { "signal": "p1" },
              "update": "{right:length(p2.right)!=0? p1.right+','+p2.right:p1.right,left:length(p2.left)!=0? p1.left+','+p2.left:p1.left}"
            }
          ]
        },
        {
          "name": "shiftArrayRunning",
          "description": "Converts a string to an array and reverses",
          "update": "{right:reverse(split(p2.right,',')),left:reverse(split(p2.left,','))}"
        }
      ],

      "data": [
        {
          "name": "table",
          "values": donutData,
          "transform": [
            {
              "type": "collect",
              "sort": {
                "field": { "signal": "sortField" },
                "order": { "signal": "sortOrder" }
              }
            },
            {
              "type": "pie",
              "field": "value",
              "startAngle": { "signal": "startAngle" },
              "endAngle": { "signal": "round((2*PI)*10000)/10000+startAngle" }
            },
            {
              "type": "formula",
              "as": "middleAngle",
              "expr": " (((datum.endAngle - datum.startAngle)/2) + datum.startAngle)-PI/2"
            },
            {
              "type": "formula",
              "as": "side",
              "expr": "datum.middleAngle + PI/2  <= PI || datum.middleAngle + PI/2 >= 2*PI && datum.middleAngle + PI/2 <= PI*3?'right':'left'"
            },
            {
              "type": "formula",
              "as": "x1",
              "expr": " (outerRadius *cos(datum.middleAngle))+width/2 "
            },
            {
              "type": "formula",
              "as": "y1",
              "expr": " (outerRadius*sin(datum.middleAngle))+width/2 "
            },
            {
              "type": "formula",
              "as": "x2",
              "expr": " ((outerRadius+10)*cos(datum.middleAngle))+width/2 "
            },
            {
              "type": "formula",
              "as": "y2",
              "expr": " ((outerRadius+10)*sin(datum.middleAngle))+width/2 "
            },
            {
              "type": "formula",
              "as": "x3",
              "expr": "datum.side== 'right'?outerRadius+(width/2)+20:(width/2)-outerRadius-20"
            },
            { "type": "formula", "as": "bin", "expr": "0" }
          ]
        },
        {
          "name": "leftExt",
          "source": ["table"],
          "transform": [
            { "type": "filter", "expr": "datum.side=='left'" },
            { "type": "extent", "field": "y2", "signal": "leftExt" }
          ]
        },
        {
          "name": "rightExt",
          "source": ["table"],
          "transform": [
            { "type": "filter", "expr": "datum.side=='right'" },
            { "type": "extent", "field": "y2", "signal": "rightExt" }
          ]
        },
        {
          "name": "labelSequenceRight",
          "transform": [
            {
              "type": "sequence",
              "start": { "signal": "rightExt[0]" },
              "stop": { "signal": "(rightExt[1]+labelHeight)" },
              "step": { "signal": "labelHeight" },
              "as": "bin"
            },
            { "type": "formula", "expr": "'right'", "as": "side" }
          ]
        },
        {
          "name": "labelSequenceLeft",
          "transform": [
            {
              "type": "sequence",
              "start": { "signal": "leftExt[0]" },
              "stop": { "signal": "(leftExt[1]+labelHeight)" },
              "step": { "signal": "labelHeight" },
              "as": "bin"
            },
            { "type": "formula", "expr": "'left'", "as": "side" }
          ]
        },
        {
          "name": "labelBinsRight",
          "source": ["rightExt", "labelSequenceRight"],
          "transform": [
            {
              "type": "bin",
              "field": "y2",
              "step": { "signal": "labelHeight" },
              "extent": { "signal": "rightExt" },
              "interval": false,
              "as": ["binTemp", "bin1"],
              "nice": false
            },
            {
              "type": "formula",
              "expr": "datum.bin==0?datum.binTemp:datum.bin",
              "as": "bin"
            }
          ]
        },
        {
          "name": "labelBinsLeft",
          "source": ["leftExt", "labelSequenceLeft"],
          "transform": [
            {
              "type": "bin",
              "field": "y2",
              "step": { "signal": "labelHeight" },
              "extent": { "signal": "leftExt" },
              "interval": false,
              "as": ["binTemp", "bin1"],
              "nice": false
            },
            {
              "type": "formula",
              "expr": "datum.bin==0?datum.binTemp:datum.bin",
              "as": "bin"
            }
          ]
        },
        {
          "name": "labelPositions",
          "source": ["labelBinsLeft", "labelBinsRight"],
          "transform": [
            {
              "type": "joinaggregate",
              "ops": ["count"],
              "as": ["count"],
              "groupby": ["side", "bin"]
            },
            { "type": "filter", "expr": "datum.value != null || datum.count == 1" },
            { "type": "formula", "expr": "datum.count-1", "as": "count" },
            {
              "type": "collect",
              "sort": {
                "field": ["side", "bin"],
                "order": ["descending", "ascending"]
              }
            },
            {
              "type": "window",
              "sort": { "field": ["bin", "y2"], "order": ["ascending", "ascending"] },
              "ops": ["row_number"],
              "as": ["index"],
              "groupby": ["side"]
            },
            {
              "type": "window",
              "sort": { "field": "index", "order": "ascending" },
              "ops": ["row_number"],
              "groupby": ["side", "bin"]
            },
            {
              "type": "formula",
              "expr": "datum.value==0?0:datum.row_number",
              "as": "row_number"
            },
            {
              "type": "formula",
              "expr": "datum.value==null?-labelHeight:datum.row_number==1?0:labelHeight",
              "as": "shift"
            },
            {
              "type": "collect",
              "sort": {
                "field": ["side", "index"],
                "order": ["descending", "ascending"]
              }
            },
            {
              "type": "joinaggregate",
              "ops": ["values"],
              "as": ["shiftArray"],
              "fields": ["shift"],
              "groupby": ["side"]
            }
          ]
        },
        {
          "name": "labelPositionsFinal",
          "source": ["labelPositions"],
          "transform": [
            {
              "type": "collect",
              "sort": {
                "field": ["side", "index"],
                "order": ["descending", "ascending"]
              }
            },
            {
              "type": "formula",
              "expr": "datum.side=='right'?toNumber(shiftArrayRunning.right[datum.index-1]):toNumber(shiftArrayRunning.left[datum.index-1])",
              "as": "shiftArrayRunning"
            },
            {
              "type": "formula",
              "expr": "datum.shiftArrayRunning+datum.bin",
              "as": "binShifted"
            },
            {
              "type": "formula",
              "as": "x4",
              "expr": "datum.side== 'right'?outerRadius+(width/2)+25:(width/2)-outerRadius-25"
            },
            {
              "type": "window",
              "sort": { "field": "index", "order": "ascending" },
              "ops": ["lead"],
              "fields": ["shift"],
              "groupby": ["side"]
            },
            {
              "type": "formula",
              "as": "y4",
              "expr": "datum.y2>datum.binShifted&&datum.lead_shift<0?datum.y2:datum.binShifted"
            },
            {
              "type": "formula",
              "as": "labelPath",
              "expr": "datum.value==null?'': 'M '+ datum.x1 + ' ' + datum.y1 + 'L'+ datum.x2 + ' ' + datum.y2 + ' H'+ datum.x3  +'L '+ datum.x4 + ' ' + datum.y4 "
            }
          ]
        }
      ],

      "scales": [
        {
          name: "color",
          type: "ordinal",
          domain: { data: "table", field: customColorDomain ? customColorDomain : "id" },
          // domain: { data: "table", field: "id" },
          range: { data: "table", field: "colorValue" }
        }
      ],

      "marks": [
        {
          "type": "arc",
          "from": { "data": "table" },
          "encode": {
            "enter": {
              "fill": { "field": "colorValue" },
              "x": { "signal": "width / 2" },
              "y": { "signal": "height / 2" },
              "innerRadius": { "signal": "innerRadius" },
              "outerRadius": { "signal": "outerRadius" },
              "tooltip": { "field": "label" }
            },
            "update": {
              "startAngle": { "field": "startAngle" },
              "endAngle": { "field": "endAngle" },
              "padAngle": { "value": breaked_donut ? 0.02 : 0 },
              "innerRadius": { "signal": "innerRadius" },
              "outerRadius": { "signal": "outerRadius" }
            }
          }
        },
        {
          "type": "path",
          "name": "labelPath",
          "from": { "data": "labelPositionsFinal" },
          "encode": {
            "update": {
              "strokeWidth": { "value": 1 },
              "path": { "field": "labelPath" },
              "stroke": { "value": "grey" },
              "opacity": { "signal": "datum.value === 0 ? 0 : 0.7" }
            }
          }
        },
        {
          "type": "rect",
          "from": { "data": "labelPositionsFinal" },
          "description": "Debug boxes. Opacity is set to zero but can be changed to better understand how the bins are being layed out",
          "encode": {
            "update": {
              "x": { "field": "x4" },
              "y": { "field": "y4" },
              "width": { "value": 20 },
              "height": { "signal": "labelHeight" },
              "strokeWidth": { "value": 2 },
              "stroke": { "value": "grey" },
              "fill": { "value": "#4682b4" },
              "opacity": { "signal": "datum.value==null?0:0" }
            }
          }
        },
        {
          "type": "text",
          "name": "labels",
          "from": { "data": "labelPositionsFinal" },
          "encode": {
            "update": {
              "text": { "field": "label" },
              "opacity": { "signal": "datum.value === 0 ? 0 : 1" },
              "fontSize": { "value": 10 },
              "fontFamily": { "value": "Arial" },
              "fill": { "value": "black" },
              "baseline": { "value": "bottom" },
              "align": { "signal": "datum.side=='left'?'right':'left'" },
              "dx": { "signal": "datum.side=='left'?-3:3" },
              "x": { "field": "x4" },
              "y": { "field": "y4" },
              "labelOverlap": "parity",
              // Add word wrapping for labels
              "lineHeight": { "value": 1 },
              // "limit": { "signal": "datum.side=='left'?80:80" }, // Limit text width to prevent overflow
              "wordBreak": "break-word"
            }
          }
        }
      ],

      "labelOverlap": "parity",
    };

    if (legendTitle) {
      Object.assign(spec, {
        "legends": [
          {
            "fill": "color",
            "title": legendTitle || "",
            "orient": "right",
            "titleFontSize": 10,
            "titleFontFamily": "Arial",
            "titleColor": "black",
            "padding": 0,
            "encode": {
              "labels": {
                "update": {
                  "text": { "field": "label" },
                  "opacity": { "value": 1 },
                  "fontSize": { "value": 10 },
                  "fontFamily": { "value": "Arial" },
                  "fill": { "value": "black" },
                }
              }
            }
          }
        ]
      });
    }

    // Clear previous chart
    chartRef.current.innerHTML = '';

    embed(chartRef.current as unknown as HTMLElement, spec, {
      actions: {
        export: true,
        source: false,
        compiled: false,
        editor: false
      }
    }).then((vegaEmbedResult: any) => {
      viewRef.current = vegaEmbedResult.view;
      const menu = chartRef.current?.querySelector(".vega-actions");
      if (menu) {
        const svgButton = menu.querySelector('a[title="Save as SVG"]');
        if (svgButton) {
          svgButton.remove();
        }
      }
    });

  }, [donutData, legendTitle, breaked_donut, calculateOptimalRadius]);

  // Debounced resize handler
  const handleResize = useCallback(() => {
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }

    resizeTimeoutRef.current = setTimeout(() => {
      renderChart();
    }, 300);
  }, [renderChart]);

  useEffect(() => {

    renderChart();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [renderChart, handleResize]);


  return (
    <div>

      <TooltipCustom title="Export as PNG">
        <ButtonSimple
          onClick={() => handleExport(viewRef, chartData?.title)}
          className='flex justify-self-end mb-1'
        >
          <IconAiOutlineDownload />
        </ButtonSimple>
      </TooltipCustom>

      <div
        className="!p-0 w-full h-full flex justify-center items-center shrink-0  "
        ref={chartRef}
        id="chart-donut"
      />

    </div>
  );
}

export default ChartDonut;